<template>
  <w-main-template>
    <w-chart-analysis-tree slot="sideContent" @clickNode="onClickTreeNode" @dragTreeNode="onDragNode" @dragendTreeNode="onDragEndNode" />
    <w-main-title slot="mainTitle" :title="templateInfo.templateName">
      <div slot="rightTool">
        <el-tooltip content="확대" placement="bottom">
          <el-button class="customised-icon expand" type="content" size="medium" @click="onClickExpandButton"></el-button>
        </el-tooltip>
        <el-tooltip v-show="!showTemplateDescView" content="엑셀출력" placement="right">
          <el-button class="customised-icon excel" type="content" size="medium" @click="exportChartExcel"></el-button>
        </el-tooltip>
        <w-datetime-range-button />
        <w-report-button />
      </div>
    </w-main-title>
    <div class="main-height" slot="mainContent">
      <el-row class="btn-area-top">
        <el-col class="btn-group-right">
          <el-button type="secondary" size="mini" :disabled="showTemplateDescView" @click="onAddTemplateButton">새로운 템플릿</el-button>
          <el-button v-if="templateInfo.templateSeq === null || templateInfo.templateSeq === undefined" size="mini" :disabled="disabledSaveButton" @click.native="onClickSaveButton('save')">
            저장하기
          </el-button>
          <el-dropdown v-else trigger="click">
            <el-button size="mini" :disabled="disabledSaveButton"> 저장하기 <i class="el-icon-arrow-down el-icon--right" /> </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="onClickSaveButton('save')">저장하기</el-dropdown-item>
              <el-dropdown-item @click.native="onClickSaveButton('saveas')">다른 이름으로 저장하기</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button size="mini" @click="onClickImportButton">불러오기</el-button>
        </el-col>

        <!-- 템플릿 저장 팝업창 -->
        <w-add-template-popup ref="addTemplatePopup" :save-type="saveType" :template-info="templateInfo" :chart-list="chartRscList" @saveTemplate="setTemplateInfo" />
      </el-row>
      <div class="left-col-wrapper">
        <!-- 자원 목록 -->
        <w-rsc-tree ref="rscTree" :selected-tree-node="selectedTreeNode" @dragstart="onDragNode" @dragend="onDragEndNode" @closeTree="onClickRscCloseButton" />
        <!-- 차트 영역 -->
        <div class="page-container-right-col">
          <div class="full-wrapper" :style="chartWrapperStyle">
            <el-scrollbar :ref="scrollbarRef">
              <div class="full" style="position:relative;">
                <w-chart-template-view v-show="showTemplateDescView" />
                <div class="drop-wrapper active" :style="showTemplateDescView ? 'position:absolute; top:20px; left:0px; width:100%;' : null">
                  <w-chart-drop-zone-view
                    ref="dropzoneView"
                    :visible="!showTemplateDescView"
                    :dropzoneVisible="dropzoneVisible"
                    :selectedTreeNode="selectedTreeNode"
                    @setChartList="setChartList"
                    @setTemplateInfo="setTemplateInfo"
                    @showSkccChartView="openSkccChartView"
                  />
                </div>
              </div>
            </el-scrollbar>
          </div>
        </div>
        <w-scroll-top-button :visible="enableScollTop" @move-scroll-top="moveScrollTop" />
      </div>
      <w-expanded-chart-view ref="expandedChart" :templateName="templateInfo.templateName" :chartList="chartRscList" />
      <w-resource-smart-analyzer-popup />
      <w-skcc-chart-view ref="skccChart" @closeSkccChartView="onCloseSkccChartView" />
    </div>
  </w-main-template>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { Drag } from 'vue-drag-drop';
import { filter, forEach, find, map, clone, partialRight, pick, pullAllWith, union, isNil, isEqual, cloneDeep } from 'lodash';

import msgUtil from '@/utils/message';
import { isEmpty } from '@/utils/basic';
import ScrollbarMixin from '@/mixins/scrollbar';
import ChartAPI from '@/api/chart/chart-analysis';
import WMainTemplate from '@/components/template/MainTemplate';
import WMainTitle from '@/components/template/MainTitle';
import WDatetimeRangeButton from '@/components/button/DatetimeRangeButton';
import WReportButton from '@/components/button/ReportButton';
import WResourceSmartAnalyzerPopup from '@/components/popup/ResourceSmartAnalyzerPopup';
import WChartAnalysisTree from './components/tree';
import WChartTemplateView from './components/view/ChartTemplateView';
import WAddTemplatePopup from './components/popup/AddTemplatePopup';
import WChartDropZoneView from './components/view/ChartDropZoneView';
import WExpandedChartView from './components/view/ExpandedChartView';
import WSkccChartView from './components/view/customize/SkccChartView';
import WRscTree from './components/tree/RscTree';

const DEFAULT_TEMPLATE_NAME = '새로운 차트 템플릿';

export default {
  name: 'WChart',
  mixins: [ScrollbarMixin],
  components: {
    Drag,
    WMainTemplate,
    WChartAnalysisTree,
    WChartTemplateView,
    WAddTemplatePopup,
    WChartDropZoneView,
    WExpandedChartView,
    WMainTitle,
    WDatetimeRangeButton,
    WReportButton,
    WResourceSmartAnalyzerPopup,
    WSkccChartView,
    WRscTree,
  },
  data() {
    return {
      clickedNode: false,
      chartList: [],
      selectedTreeNode: {},
      showRsc: false,
      templateInfo: {
        templateName: DEFAULT_TEMPLATE_NAME,
      },
      saveType: null,
      dropzoneVisible: false,
      chartRscList: [],
      realtimeFlag: false,
      showSkccChartView: false,
      stopDatetimeRangeChangeEvent: false,
    };
  },
  computed: {
    showTemplateDescView() {
      return isEmpty(this.chartRscList);
    },
    chartWrapperStyle() {
      return { marginLeft: this.showRsc ? '230px' : 0 };
    },
    /**
     * [저장하기] 버튼 활성/비활성화 여부
     * @return {[type]} [description]
     * @author jda
     */
    disabledSaveButton() {
      return isEmpty(this.chartRscList);
    },
    ...mapGetters(['analysisDateParams', 'isRelativeAnalysisDate']),
    ...mapState({
      analysisDateData: (state) => state.performance.analysisDateData,
      initChartTemplateSeq: (state) => state.chartAnalysis.initChartTemplateSeq,
    }),
  },
  watch: {
    initChartTemplateSeq: {
      immediate: true,
      handler(val) {
        if (!isNil(val)) {
          this.loadChartTemplateInfo(val);
          this.$store.dispatch('setInitChartTemplateSeq', null);
        }
      },
    },
  },
  mounted() {
    this.$store.dispatch('setInitialDatetimeRange', {
      type: 'interval',
      interval: 'hour',
      value: 12,
    });
    this.$store.dispatch('setPlotLineDatetime', null);
    this.setScrollData();
  },
  /**
   * 라우터 변경되기 직전 beforeRouteLeave 훅
   * @author jeee12
   * @param  {[type]}   to   [description]
   * @param  {[type]}   from [description]
   * @param  {Function} next [description]
   */
  beforeRouteLeave(to, from, next) {
    next(false); // 브라우저 뒤로가기에서 url 변경이 즉시되는 현상 차단
    this.checkToSaveChartTemplate()
      .then((resolve) => {
        // 저장 여부 체크 후 resolve로 라우터 이동 진행 여부 판단
        next(resolve);
      })
      .catch(() => {
        next(false);
      });
  },
  methods: {
    isNil,
    /**
     * 트리 노드 선택시 해당 관리대상의 자원 정보들을 가져온다.
     * @param  {[type]} data [description]
     * @param  {[type]} node [description]
     * @return {[type]}      [description]
     * @author jda
     */
    onClickTreeNode(data) {
      this.selectedTreeNode = data;

      const isObj = this.isObj(data);
      if (!isObj) {
        return;
      }
      this.clickedNode = isObj;
      this.showRsc = isObj;

      this.$refs.rscTree.showTree();
    },
    /**
     * [새로운 템플릿] 버튼 클릭시 차트 영역이 초기화된다.
     * @return {[type]} [description]
     * @author jda
     */
    onAddTemplateButton() {
      this.checkToSaveChartTemplate()
        .then((resolve) => {
          if (resolve) {
            this.chartRscList = [];
            this.templateInfo = {
              templateName: DEFAULT_TEMPLATE_NAME,
            };
          }
        })
        .catch(() => {});
    },
    /**
     * [저장하기] 버튼 클릭시
     * @return {[type]} [description]
     * @author jda
     */
    onClickSaveButton(type) {
      this.saveType = type;
      if (type === 'save' && !isEmpty(this.templateInfo.templateSeq)) {
        this.saveModifiedChartTemplate();
      } else {
        this.$refs.addTemplatePopup.open();
      }
    },
    /**
     * 템플릿 저장후 저장한 템플릿 이름을 TopLabel에 표현한다.
     * @return {[type]} [description]
     * @author jda
     */
    setTemplateInfo(templateInfo) {
      // 템플릿 저장 후 추가,삭제된 series의 플래그 값 초기화 (변경사항 감지를 위해)
      forEach(templateInfo.charts, (chartObject) => {
        if (!isEmpty(chartObject.chartSeq)) {
          const addedSeries = filter(chartObject.items, 'added');
          if (!isEmpty(addedSeries)) {
            forEach(addedSeries, (record) => {
              delete record.added;
            });
          }
          const deletedSeries = filter(chartObject.items, 'deleted');
          if (!isEmpty(deletedSeries)) {
            forEach(deletedSeries, (record) => {
              delete record.deleted;
            });
          }
        }
      });

      this.templateInfo = templateInfo;
    },
    /**
     * [불러오기]버튼 클릭
     * @return {[type]} [description]
     * @author jda
     */
    onClickImportButton() {
      this.checkToSaveChartTemplate()
        .then((resolve) => {
          if (resolve) {
            this.$refs.dropzoneView.importPopupOpen();
          }
        })
        .catch(() => {});
    },
    /**
     * 트리나 자원 드래그 시 dropzone 생성
     * @param  {[type]} node [description]
     * @return {[type]}      [description]
     * @author jda
     */
    onDragNode(node) {
      const CHART_MAX_COUNT = 10;
      this.dropzoneVisible = true;

      const rscUnit = isEmpty(node.rscUnit) ? null : node.rscUnit;
      const isGroup = isEmpty(node.rscId) && !this.isObj(node.data);

      if (!isEmpty(node.rscId)) {
        this.setClassExistingRsc(node);
      }

      const chartContainers = document.getElementsByClassName('list-analytics');
      forEach(chartContainers, (container) => {
        if (container.className.indexOf('drop-area') < 0) {
          const chartRscUnit = container.getAttribute('rscUnit');
          if ((!isEmpty(chartRscUnit) && chartRscUnit !== rscUnit) || isGroup) {
            container.className += ' drop-area';
          } else {
            container.className += ' drop-area blue';
          }
        }
      });

      const dropzoneContainers = document.getElementsByClassName('dropzone-area');
      forEach(dropzoneContainers, (container) => {
        if (this.chartRscList.length >= CHART_MAX_COUNT || isGroup) {
          container.className += ' drop-area';
        } else {
          container.className += ' drop-area blue';
        }
      });
    },
    /**
     * 드래그 노드를 drop했을 경우 dropzone의 스타일을 해제한다.
     * @return {[type]} [description]
     * @author jda
     */
    onDragEndNode() {
      this.dropzoneVisible = false;
      const chartContainers = document.getElementsByClassName('list-analytics');
      forEach(chartContainers, (container) => {
        const className = container.className;
        container.className = className.replace(/ drop-area| blue| green/g, '');
      });

      const dropzoneContainers = document.getElementsByClassName('dropzone-area');
      forEach(dropzoneContainers, (container) => {
        const className = container.className;
        container.className = className.replace(/ drop-area| blue| green/g, '');
      });
    },
    /**
     * [확대]버튼 클릭시 차트가 한 화면에 크게 그려진다.
     * @return {[type]} [description]
     * @author jda
     */
    onClickExpandButton() {
      this.$refs.expandedChart.open();
    },
    /**
     * 그려진 차트의 정보를 공유한다.
     * @param  {[type]} chartRscList [description]
     * @author jda
     */
    setChartList(chartRscList) {
      this.chartRscList = chartRscList;
    },
    /**
     * 자원 drag시 해당 자원이 이미 추가된 차트에는 drop하지 못하도록 한다.
     * @param  {[type]} node [description]
     * @return {[type]}      [description]
     * @author jda
     */
    setClassExistingRsc(node) {
      const selectedTreeNode = this.selectedTreeNode;
      const { managetypeId, rsctypeId, rscId } = node;
      const rscInfo = {
        managetypeId,
        rsctypeId,
        rscId,
      };
      if (managetypeId !== 'E2E') {
        Object.assign(rscInfo, {
          objId: selectedTreeNode.obj.objId,
          rscKey: isEmpty(node.rscKey) ? '_' : node.rscKey,
        });
      } else {
        Object.assign(rscInfo, {
          rscKey: selectedTreeNode.rscKey,
        });
      }
      const visibleChartList = [];

      // 삭제된 시리즈를 제외한다.
      this.chartRscList.forEach((chartObject) => {
        const chart = clone(chartObject);
        const visibleSeries = filter(chartObject.items, (record) => isEmpty(record.deleted) || !record.deleted);
        chart.items = visibleSeries;
        visibleChartList.push(chart);
      });

      // 해당 자원이 이미 추가됬으면 drop하지 못한다.
      const existingRscList = filter(visibleChartList, (chartObject) => find(chartObject.items, rscInfo));
      existingRscList.forEach((record) => {
        const container = record.chart.container.closest('[class="list-analytics"]');
        container.className += ' drop-area';
      });
    },
    /**
     * 현재 화면에 보여지는 차트의 성능 정보를 엑셀로 출력한다.
     * @return {[type]} [description]
     * @author jda
     */
    exportChartExcel() {
      const items = [];
      this.chartRscList.forEach((record) => {
        if (record.visible) {
          const rscInfoList = map(record.items, partialRight(pick, ['managetypeId', 'objId', 'rsctypeId', 'rscId', 'rscKey']));
          items.push(
            Object.assign(
              {
                rscInfoList,
                isRelative: this.isRelativeAnalysisDate,
                statType: record.statType,
              },
              this.analysisDateParams,
            ),
          );
        }
      });

      const analysisDateData = this.analysisDateData.data;
      ChartAPI.exportChartExcel({
        perfParamStringData: JSON.stringify(items),
        templateName: this.templateInfo.templateName,
        conditionString: `${analysisDateData.startLabel} ~ ${analysisDateData.endLabel}`,
      });
    },
    /**
     * 자원 목록 컴포넌트를 숨김처리한다.
     * @return {[type]} [description]
     * @author jda
     */
    onClickRscCloseButton() {
      this.showRsc = false;
    },
    isObj(data) {
      return data.type === 'obj';
    },
    saveModifiedChartTemplate() {
      const chartList = this.chartRscList;
      const originalCharts = this.templateInfo.charts;
      // 추가된 차트
      let addedCharts = filter(chartList, 'added');
      pullAllWith(chartList, addedCharts);

      // 삭제된 차트
      let deletedCharts = filter(originalCharts, ['visible', false]);

      const currentOriginChart = filter(originalCharts, 'visible');
      // 추가/삭제된 시리즈
      let addedItems = [];
      let deletedItems = [];
      currentOriginChart.forEach((chartObject) => {
        if (!isEmpty(chartObject.chartSeq)) {
          const addedSeries = filter(chartObject.items, 'added');
          map(addedSeries, (record) => {
            record.chartSeq = chartObject.chartSeq;
          });
          const deletedSeries = filter(chartObject.items, 'deleted');

          addedItems = union(addedItems, addedSeries);
          deletedItems = union(deletedItems, deletedSeries);
        }
      });

      addedCharts = map(addedCharts, partialRight(pick, ['items', 'orderbyIndex']));
      deletedCharts = map(deletedCharts, partialRight(pick, ['templateSeq', 'chartSeq']));

      const target = '템플릿';
      const action = msgUtil.action.SAVE;
      ChartAPI.updatePerfChartTemplate({
        templateSeq: this.templateInfo.templateSeq,
        templateName: this.templateInfo.templateName,
        addedCharts,
        deletedCharts,
        addedItems,
        deletedItems,
      })
        .then((response) => {
          if (response.data.success) {
            this.$message(msgUtil.getResultMsgData(action, target, true));
            this.templateInfo.charts = chartList;
            this.setTemplateInfo(this.templateInfo);
          } else {
            this.$message(msgUtil.getResultMsgData(action, target, false));
          }
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        });
    },
    /**
     * SKCC 원본 데이터 차트 팝업을 나타낸다.
     *
     * @author gimang
     */
    openSkccChartView(chartInfo) {
      if (!isEmpty(chartInfo.items) && chartInfo.items.length > 0) {
        this.$refs.skccChart.open(chartInfo.items[0]);
        this.stopDatetimeRangeChangeEvent = true;
      }
    },
    onCloseSkccChartView() {
      this.$refs.dropzoneView.closeSkccChartView();
    },
    /**
     * 변경 사항이 있는 템플릿에 대해 저장을 유도한다.
     * @author jeee12
     * @return {[type]} [description]
     */
    checkToSaveChartTemplate() {
      // 새로운 템플릿, 기존 템플릿 불러오기, 화면 전환 (route)
      return new Promise((resolve) => {
        const isChanged = this.checkChangeTemplateInfo();
        const checkSave = isChanged && !this.showTemplateDescView;
        if (checkSave) {
          const messageOptions = {
            closeOnClickModal: false,
            closeOnHashChange: false,
            ...msgUtil.defaultConfirmButton,
          };
          this.$confirm('변경 사항이 있습니다. 저장하시겠습니까?', '확인', messageOptions)
            .then(() => {
              // 저장 작업 수행
              this.onClickSaveButton('save');
              resolve(false); // 기존 액션 진행 false
            })
            .catch(() => {
              // 저장 작업 취소
              resolve(true); // 기존 액션 진행 true
            });
        } else {
          resolve(true); // 기존 액션 진행 true
        }
      });
    },
    /**
     * 차트 템플릿의 변경된 사항 확인
     * @author jeee12
     * @return {[type]} [description]
     */
    checkChangeTemplateInfo() {
      const chartList = cloneDeep(this.chartRscList);
      const originalCharts = cloneDeep(this.templateInfo.charts);

      const chartKeys = [];
      if (!isNil(chartList)) {
        chartList.forEach((chart) => {
          chart.items.forEach((item) => {
            chartKeys.push(`${item.managetypeId}.${item.objId}.${item.rsctypeId}.${item.rscId}.${item.rscKey}.${item.orderbyIndex}`);
          });
        });
      }
      const originalChartKeys = [];
      if (!isNil(originalCharts)) {
        originalCharts.forEach((chart) => {
          chart.items.forEach((item) => {
            originalChartKeys.push(`${item.managetypeId}.${item.objId}.${item.rsctypeId}.${item.rscId}.${item.rscKey}.${item.orderbyIndex}`);
          });
        });
      }
      // 추가/삭제된 차트의 존재시 시리즈 체크 이전에 return
      if (!isEqual(chartKeys, originalChartKeys)) {
        return true;
      }

      let isSeriesChanged = false;
      const currentOriginChart = filter(originalCharts, 'visible');
      // 추가/삭제된 시리즈
      currentOriginChart.some((chartObject) => {
        if (!isEmpty(chartObject.chartSeq)) {
          const addedSeries = filter(chartObject.items, 'added');
          map(addedSeries, (record) => {
            record.chartSeq = chartObject.chartSeq;
          });
          const deletedSeries = filter(chartObject.items, 'deleted');
          isSeriesChanged = !isEmpty(addedSeries) || !isEmpty(deletedSeries);
          return isSeriesChanged;
        }
        return isEmpty(chartObject.chartSeq);
      });

      return isSeriesChanged;
    },
    /**
     * 차트 템플릿 데이터를 로드한다.
     * @author Ju
     * @param  {Number} templateSeq 차트 템플릿 시퀀스 번호
     */
    loadChartTemplateInfo(templateSeq) {
      ChartAPI.getPerfChartTemplate(templateSeq)
        .then((response) => {
          if (response.data.success) {
            this.$refs.dropzoneView.resetChart(response.data.data);
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '차트 템플릿 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '차트 템플릿 정보', false));
          throw new Error(e);
        });
    },
  },
};
</script>

<style scoped>
.h100 {
  height: 100%;
}

.content-height {
  height: calc(100% - 42px);
}

.main-height {
  height: calc(100% - 35px);
}

.scrollable {
  overflow-y: auto;
}
</style>



// WEBPACK FOOTER //
// src/views/chart/index.vue